import React, { useState, useEffect } from 'react';
import { Languages, Eye, Edit3, Check, X, RotateCcw, AlertCircle, Clock, CheckCircle, Globe } from 'lucide-react';
import toast from 'react-hot-toast';

const AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
  { code: 'ru', name: 'Russian', flag: '🇷🇺' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' }
];

const TranslationStatusIcon = ({ status }) => {
  const iconProps = { className: "w-4 h-4" };
  
  switch (status) {
    case 'pending':
      return <Clock {...iconProps} className="w-4 h-4 text-yellow-500" />;
    case 'translated':
      return <CheckCircle {...iconProps} className="w-4 h-4 text-green-500" />;
    case 'manual_review':
      return <AlertCircle {...iconProps} className="w-4 h-4 text-orange-500" />;
    case 'error':
      return <X {...iconProps} className="w-4 h-4 text-red-500" />;
    case 'skipped':
      return <Eye {...iconProps} className="w-4 h-4 text-gray-400" />;
    default:
      return <Clock {...iconProps} className="w-4 h-4 text-gray-400" />;
  }
};

const TranslationEditor = ({ section, language, originalSection, onSave, onCancel }) => {
  const [editedContent, setEditedContent] = useState(section.content);
  const [editedTitle, setEditedTitle] = useState(section.title || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(section.id, {
        title: editedTitle,
        content: editedContent,
        translationStatus: 'manual_review'
      });
      toast.success('Translation saved successfully');
    } catch (error) {
      toast.error('Failed to save translation');
    } finally {
      setSaving(false);
    }
  };

  const renderContentEditor = (content, path = '') => {
    if (!content || typeof content !== 'object') {
      return (
        <textarea
          value={content || ''}
          onChange={(e) => {
            const newContent = { ...editedContent };
            if (path) {
              const keys = path.split('.');
              let current = newContent;
              for (let i = 0; i < keys.length - 1; i++) {
                current = current[keys[i]];
              }
              current[keys[keys.length - 1]] = e.target.value;
            } else {
              setEditedContent(e.target.value);
            }
            setEditedContent(newContent);
          }}
          className="w-full text-xs border border-gray-300 rounded px-2 py-1 min-h-[60px]"
          placeholder="Enter translation..."
        />
      );
    }

    return Object.entries(content).map(([key, value]) => (
      <div key={key} className="space-y-1">
        <label className="block text-xs font-medium text-gray-700 capitalize">
          {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
        </label>
        {typeof value === 'string' ? (
          <textarea
            value={value}
            onChange={(e) => {
              const newContent = { ...editedContent };
              newContent[key] = e.target.value;
              setEditedContent(newContent);
            }}
            className="w-full text-xs border border-gray-300 rounded px-2 py-1 min-h-[40px]"
            placeholder="Enter translation..."
          />
        ) : Array.isArray(value) ? (
          <div className="space-y-1">
            {value.map((item, index) => (
              <div key={index}>
                {typeof item === 'string' ? (
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => {
                      const newContent = { ...editedContent };
                      newContent[key][index] = e.target.value;
                      setEditedContent(newContent);
                    }}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                    placeholder="Enter translation..."
                  />
                ) : (
                  <div className="border border-gray-200 rounded p-2">
                    {renderContentEditor(item, `${key}.${index}`)}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="border border-gray-200 rounded p-2">
            {renderContentEditor(value, key)}
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900">
          Edit Translation - {section.type}
        </h4>
        <div className="flex space-x-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
          >
            <Check className="w-3 h-3 mr-1" />
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={onCancel}
            disabled={saving}
            className="flex items-center px-3 py-1 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50"
          >
            <X className="w-3 h-3 mr-1" />
            Cancel
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Original Content */}
        <div className="space-y-3">
          <h5 className="text-xs font-medium text-gray-600">
            Original ({AVAILABLE_LANGUAGES.find(l => l.code === 'en')?.name})
          </h5>
          <div className="bg-gray-50 border rounded p-3 space-y-2">
            {originalSection.title && (
              <div>
                <label className="block text-xs font-medium text-gray-700">Title</label>
                <div className="text-xs text-gray-600">{originalSection.title}</div>
              </div>
            )}
            <div className="text-xs text-gray-600">
              {JSON.stringify(originalSection.content, null, 2)}
            </div>
          </div>
        </div>

        {/* Translation Editor */}
        <div className="space-y-3">
          <h5 className="text-xs font-medium text-gray-600">
            Translation ({AVAILABLE_LANGUAGES.find(l => l.code === language.code)?.name})
          </h5>
          <div className="space-y-3">
            {originalSection.title && (
              <div>
                <label className="block text-xs font-medium text-gray-700">Title</label>
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                  placeholder="Enter title translation..."
                />
              </div>
            )}
            <div className="space-y-2">
              {renderContentEditor(editedContent)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TranslationManager = ({ site, onSiteUpdate }) => {
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [loading, setLoading] = useState(false);

  const defaultLanguage = site?.defaultLanguage || 'en';
  const currentLanguages = site?.languages || [];

  const getLanguageInfo = (langCode) => {
    return AVAILABLE_LANGUAGES.find(l => l.code === langCode) || 
           { code: langCode, name: langCode.toUpperCase(), flag: '🌐' };
  };

  const getOriginalPages = () => {
    return site.pages && site.pages.length > 0 ? site.pages : 
           [{ name: 'Home', slug: 'index', sections: site.sections || [] }];
  };

  const handleRetranslateSection = async (languageCode, pageIndex, sectionIndex) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/sites/${site._id}/translate-section`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          language: languageCode,
          pageIndex,
          sectionIndex,
          forceRetranslate: true
        })
      });

      if (response.ok) {
        const updatedSite = await response.json();
        onSiteUpdate(updatedSite);
        toast.success('Section retranslated successfully');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to retranslate section');
      }
    } catch (error) {
      console.error('Error retranslating section:', error);
      toast.error('Failed to retranslate section');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTranslation = async (sectionId, translationData) => {
    const response = await fetch(`/api/sites/${site._id}/translations`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        language: selectedLanguage.code,
        sectionId,
        ...translationData
      })
    });

    if (!response.ok) {
      throw new Error('Failed to save translation');
    }

    const updatedSite = await response.json();
    onSiteUpdate(updatedSite);
    setEditingSection(null);
  };

  const handleBulkTranslate = async (languageCode) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/sites/${site._id}/bulk-translate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          language: languageCode,
          onlyPending: true
        })
      });

      if (response.ok) {
        const updatedSite = await response.json();
        onSiteUpdate(updatedSite);
        const stats = getTranslationStats(selectedLanguage);
        toast.success(`Successfully translated pending sections for ${getLanguageInfo(languageCode).name}`);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to translate sections');
      }
    } catch (error) {
      console.error('Error bulk translating:', error);
      toast.error('Failed to translate sections');
    } finally {
      setLoading(false);
    }
  };

  const handleTranslateAllLanguages = async () => {
    const activeLanguages = currentLanguages.filter(lang => {
      const stats = getTranslationStats(lang);
      return lang.isActive && stats.pending > 0;
    });

    if (activeLanguages.length === 0) {
      toast.info('No pending translations found');
      return;
    }

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Translate each language sequentially to avoid overwhelming the API
      for (const language of activeLanguages) {
        try {
          const response = await fetch(`/api/sites/${site._id}/bulk-translate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              language: language.code,
              onlyPending: true
            })
          });

          if (response.ok) {
            const updatedSite = await response.json();
            onSiteUpdate(updatedSite);
            successCount++;
            console.log(`✅ Translated ${language.name} successfully`);
          } else {
            errorCount++;
            console.error(`❌ Failed to translate ${language.name}`);
          }
        } catch (error) {
          errorCount++;
          console.error(`❌ Error translating ${language.name}:`, error);
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully translated ${successCount} language(s)${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
      } else {
        toast.error('Failed to translate any languages');
      }
    } catch (error) {
      console.error('Error in bulk translate all:', error);
      toast.error('Failed to start translation process');
    } finally {
      setLoading(false);
    }
  };

  const getTranslationStats = (language) => {
    if (!language.pages) return { total: 0, translated: 0, pending: 0, errors: 0 };
    
    const sections = language.pages.flatMap(page => page.sections || []);
    const total = sections.length;
    const translated = sections.filter(s => s.translationStatus === 'translated').length;
    const pending = sections.filter(s => !s.translationStatus || s.translationStatus === 'pending').length;
    const errors = sections.filter(s => s.translationStatus === 'error').length;
    
    console.log(`🔍 Frontend stats for ${language.code}:`, {
      total,
      translated,
      pending,
      errors,
      sectionStatuses: sections.map(s => ({ type: s.type, status: s.translationStatus }))
    });
    
    return { total, translated, pending, errors };
  };

  if (currentLanguages.length === 0) {
    return (
      <div className="text-center py-8">
        <Languages className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-sm font-medium text-gray-900 mb-2">No additional languages</h3>
        <p className="text-xs text-gray-500">Add languages to enable translation management</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Languages className="w-5 h-5 mr-2 text-gray-500" />
          <h3 className="text-sm font-medium text-gray-900">Translation Manager</h3>
        </div>
        
        {/* Bulk Actions for All Languages */}
        <div className="flex items-center space-x-2">
          {(() => {
            const totalPending = currentLanguages.reduce((total, lang) => {
              const stats = getTranslationStats(lang);
              return total + stats.pending;
            }, 0);
            
            if (totalPending > 0) {
              return (
                <button
                  onClick={handleTranslateAllLanguages}
                  disabled={loading}
                  className="flex items-center px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                  title={`Translate all ${totalPending} pending sections across all languages`}
                >
                  <Globe className="w-3 h-3 mr-1" />
                  {loading ? 'Translating...' : `Translate All (${totalPending})`}
                </button>
              );
            }
            return null;
          })()}
        </div>
      </div>

      {/* Language Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {currentLanguages.map((language) => {
          const langInfo = getLanguageInfo(language.code);
          const stats = getTranslationStats(language);
          const completionRate = stats.total > 0 ? Math.round((stats.translated / stats.total) * 100) : 0;
          
          return (
            <div
              key={language.code}
              className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                selectedLanguage?.code === language.code
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedLanguage(language)}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <span className="text-lg mr-2">{langInfo.flag}</span>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{langInfo.name}</div>
                    <div className="text-xs text-gray-500">
                      {language.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-gray-900">{completionRate}%</div>
                  <div className="text-xs text-gray-500">Complete</div>
                </div>
              </div>
              
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span>Progress</span>
                  <span>{stats.translated}/{stats.total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${completionRate}%` }}
                  />
                </div>
              </div>
              
              {(stats.pending > 0 || stats.errors > 0) && (
                <div className="flex items-center justify-between mt-2 text-xs">
                  {stats.pending > 0 && (
                    <span className="text-yellow-600">{stats.pending} pending</span>
                  )}
                  {stats.errors > 0 && (
                    <span className="text-red-600">{stats.errors} errors</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Translation Details */}
      {selectedLanguage && (
        <div className="border border-gray-200 rounded-lg">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-900">
                {getLanguageInfo(selectedLanguage.code).flag} {getLanguageInfo(selectedLanguage.code).name} Translation
              </h4>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  selectedLanguage.translationStatus === 'completed' ? 'bg-green-100 text-green-800' :
                  selectedLanguage.translationStatus === 'syncing' ? 'bg-blue-100 text-blue-800' :
                  selectedLanguage.translationStatus === 'error' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {selectedLanguage.translationStatus || 'pending'}
                </span>
                
                {/* Bulk Translation Actions */}
                {(() => {
                  const stats = getTranslationStats(selectedLanguage);
                  if (stats.pending > 0) {
                    return (
                      <button
                        onClick={() => handleBulkTranslate(selectedLanguage.code)}
                        disabled={loading}
                        className="flex items-center px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                        title={`Translate ${stats.pending} pending sections`}
                      >
                        <RotateCcw className="w-3 h-3 mr-1" />
                        {loading ? 'Translating...' : `Translate ${stats.pending}`}
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          </div>

          <div className="p-4">
            {editingSection ? (
              <TranslationEditor
                section={editingSection.translated}
                language={selectedLanguage}
                originalSection={editingSection.original}
                onSave={handleSaveTranslation}
                onCancel={() => setEditingSection(null)}
              />
            ) : (
              <div className="space-y-4">
                {getOriginalPages().map((originalPage, pageIndex) => {
                  const translatedPage = selectedLanguage.pages?.[pageIndex];
                  
                  return (
                    <div key={pageIndex} className="space-y-3">
                      <h5 className="text-sm font-medium text-gray-900">{originalPage.name}</h5>
                      <div className="space-y-2">
                        {originalPage.sections?.map((originalSection, sectionIndex) => {
                          const translatedSection = translatedPage?.sections?.[sectionIndex];
                          const status = translatedSection?.translationStatus || 'pending';
                          
                          return (
                            <div
                              key={sectionIndex}
                              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                            >
                              <div className="flex items-center space-x-3">
                                <TranslationStatusIcon status={status} />
                                <div>
                                  <div className="text-sm font-medium text-gray-900 capitalize">
                                    {originalSection.type}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {originalSection.title && `"${originalSection.title}"`}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => setEditingSection({
                                    original: originalSection,
                                    translated: translatedSection || { 
                                      ...originalSection, 
                                      content: {}, 
                                      translationStatus: 'pending' 
                                    }
                                  })}
                                  className="p-1 text-blue-600 hover:text-blue-800"
                                  title="Edit translation"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>
                                
                                <button
                                  onClick={() => handleRetranslateSection(
                                    selectedLanguage.code, 
                                    pageIndex, 
                                    sectionIndex
                                  )}
                                  disabled={loading}
                                  className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                                  title="Retranslate section"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TranslationManager;